<script setup lang="ts">
import device from '@@/bits/device'
import { __ } from '@@/bits/intl'
import { useNativeAppStore } from '@@/pinia/native_app'
import type { UserFieldValue } from '@@/types'
import type { PostPropertyDisplayAttributes } from '@@/vuecomponents/SurfacePostProperties.vue'
import { computed, ref } from 'vue'

const props = withDefaults(
  defineProps<{
    darkMode: boolean
    postProperty: PostPropertyDisplayAttributes
    propertyValueClass?: string
    as?: string
  }>(),
  {
    propertyValueClass: 'text-body-small',
    as: 'dd',
  },
)

const nativeAppStore = useNativeAppStore()

function assertIsUserFieldValue(value: unknown): asserts value is UserFieldValue {
  if (
    typeof value !== 'object' ||
    value == null ||
    !Object.keys(value).every((key) => key === 'username' || key === 'name' || key === 'id')
  ) {
    throw new Error('Expected UserFieldValue but got: ' + typeof value)
  }
}

const getProfileUrl = (value: UserFieldValue) => {
  return `${window.location.origin}/${value.username}`
}

const userFieldValue = computed(() => {
  assertIsUserFieldValue(props.postProperty.rawValue)
  return props.postProperty.rawValue
})

const userFieldHtmlRef = ref<HTMLElement | null>(null)

const formattedName = computed<string>(() => {
  if (props.postProperty.formattedValue !== '') {
    return props.postProperty.formattedValue
  }
  return __('User not found')
})

const handleUserFieldValueClick = (event: MouseEvent) => {
  if (device.app) {
    event.preventDefault()
    nativeAppStore.openLink({
      url: getProfileUrl(userFieldValue.value),
      linkType: 'internal',
      contentCategory: 'page',
      contentSubcategory: 'profile',
      username: userFieldValue.value.username,
    })
  }
  // web will handle the default anchor tag behavior
}
</script>

<template>
  <!-- eslint-disable vue/no-v-html vue/no-v-text-v-html-on-component -->
  <component
    :is="as"
    ref="userFieldHtmlRef"
    :class="[
      'user-field-value',
      propertyValueClass,
      darkMode && 'text-light-text-100',
      !darkMode && 'text-dark-text-100',
    ]"
  >
    <a
      :href="getProfileUrl(userFieldValue)"
      target="_blank"
      rel="noopener nofollow ugc"
      data-testid="surfacePostPropertiesUserFieldValue"
      @click="handleUserFieldValueClick"
      >{{ formattedName }}</a
    >
  </component>
</template>
<style>
.user-field-value a {
  @apply underline;
  @apply text-current;
  text-underline-position: from-font;
  text-decoration-thickness: from-font;
}
</style>
